import { useEffect, useState } from 'react'
import { firestore } from '../../firebase'
import { SearchOutlined } from '@ant-design/icons'
import { Table, Input, Space, Button, Statistic, Row, Col, Card, Typography, Divider, DatePicker, Tooltip, Skeleton } from 'antd'
import { CSVLink } from 'react-csv'
import Highlighter from 'react-highlight-words'
import moment from 'moment'
import DateRangeSelector from '../dateRangeSelector'
import { affiliates } from '../../helpers/affiliates'

const { Title, Text } = Typography

const LeadsIn = () => {

  const [isFetching, setIsFetching] = useState(false)
  const [historyTable, setHistoryTable] = useState([])
  const [filteredTable, setTotalFiltered] = useState([])
  const [searchText, setSearchText] = useState(null)
  const [searchedColumn, setSearchedColumn] = useState(null)
  const [fetchStartDate, setFetchStartDate] = useState(null)
  const [fetchEndDate, setFetchEndDate] = useState(null)
  const [filters, setFilters] = useState({ list: null, source_name: null, offer: null, zeroBounce: null })
  const [groupedTotals, setGroupedTotals] = useState({ list: null, source_name: null, offer: null, zeroBounce: null })

  useEffect(() => {

    if (fetchStartDate && fetchEndDate) fetchHistory()

  }, [fetchStartDate, fetchEndDate])

  const sourceNameLookup = (affiliateId) => {
    return affiliates.find(x => x.affiliate_id === affiliateId)?.name || 'n/a'
  }

  const determineAllUnqiue = list => {

    const unique = new Set()

    list.forEach((obj) => {
      unique.add(obj.email)
    })

    return unique.size
  }

  const applyFilters = async (tableData, filters = {}) => {

    const filteredTableData = tableData
      .filter(x => !Object.entries(filters)
        .reduce((exclude, [k, v]) => exclude || v && !v.includes(x[k]), false))

    // lists

    const groupedLists = tableData
      .reduce((acc, obj) => {

        const category = obj.list
        const existingArray = acc.find((arr) => arr[0]?.list === category)

        if (existingArray) {
          existingArray.push(obj)
        } else {
          acc.push([obj])
        }

        return acc
      }, [])

    const filteredExceptList = tableData
      .filter(x => !Object.entries(filters)
        .filter(([k, v]) => k !== 'list')
        .reduce((exclude, [k, v]) => exclude || v && !v.includes(x[k]), false))

    const groupedFilteredLists = filteredExceptList
      .reduce((acc, obj) => {

        const category = obj.list
        const existingArray = acc.find((arr) => arr[0]?.list === category)

        if (existingArray) {
          existingArray.push(obj)
        } else {
          acc.push([obj])
        }

        return acc
      }, [])

    const listTotals = groupedLists.sort((a, b) => b.length - a.length).map((x, i) => {
      const filtered = groupedFilteredLists.find(y => y[0].list === x[0].list)
      return {
        name: x[0].list,
        total: filtered?.length || 0,
        unique: filtered?.length ? determineAllUnqiue(filtered) : 0,
      }
    })

    // partners

    const groupedSourceNames = tableData
      .reduce((acc, obj) => {

        const category = obj.source_name
        const existingArray = acc.find((arr) => arr[0]?.source_name === category)

        if (existingArray) {
          existingArray.push(obj)
        } else {
          acc.push([obj])
        }

        return acc
      }, [])

    const filteredExceptSourceName = tableData
      .filter(x => !Object.entries(filters)
        .filter(([k, v]) => k !== 'source_name')
        .reduce((exclude, [k, v]) => exclude || v && !v.includes(x[k]), false))

    const groupedFilteredSourceName = filteredExceptSourceName
      .reduce((acc, obj) => {

        const category = obj.source_name
        const existingArray = acc.find((arr) => arr[0]?.source_name === category)

        if (existingArray) {
          existingArray.push(obj)
        } else {
          acc.push([obj])
        }

        return acc
      }, [])

    const sourceNameTotals = groupedSourceNames.sort((a, b) => b.length - a.length).map((x, i) => {
      const filtered = groupedFilteredSourceName.find(y => y[0].source_name === x[0].source_name)
      return {
        name: x[0].source_name,
        title: `${x[0].source_name} (affiliate ${x[0].affiliate})`,
        total: filtered?.length || 0,
        unique: filtered?.length ? determineAllUnqiue(filtered) : 0,
      }
    })

    // Offers

    const groupedOffers = tableData
      .reduce((acc, obj) => {

        const category = obj.offer
        const existingArray = acc.find((arr) => arr[0]?.offer === category)

        if (existingArray) {
          existingArray.push(obj)
        } else {
          acc.push([obj])
        }

        return acc
      }, [])

    const filteredExceptOffer = tableData
      .filter(x => !Object.entries(filters)
        .filter(([k, v]) => k !== 'offer')
        .reduce((exclude, [k, v]) => exclude || v && !v.includes(x[k]), false))

    const groupedFilteredOffers = filteredExceptOffer
      .reduce((acc, obj) => {

        const category = obj.offer
        const existingArray = acc.find((arr) => arr[0]?.offer === category)

        if (existingArray) {
          existingArray.push(obj)
        } else {
          acc.push([obj])
        }

        return acc
      }, [])

    const offerTotals = groupedOffers.sort((a, b) => b.length - a.length).map((x, i) => {
      const filtered = groupedFilteredOffers.find(y => y[0].offer === x[0].offer)
      return {
        name: x[0].offer,
        total: filtered?.length || 0,
        unique: filtered?.length ? determineAllUnqiue(filtered) : 0,
      }
    })

    setHistoryTable(tableData)
    setTotalFiltered(filteredTableData)
    setGroupedTotals({ list: listTotals, source_name: sourceNameTotals, offer: offerTotals })
  }

  const fetchHistory = async () => {

    setIsFetching(true)

    try {

      // list of leads
      const res = await firestore.collection('sms')
        .where('created_at', '>=', new Date(fetchStartDate.startOf('d').valueOf()))
        .where('created_at', '<=', new Date(fetchEndDate.endOf('d').valueOf()))
        .orderBy('created_at', 'desc')
        .get()

      if (res.empty) return

      // --- ALL ---
      const tableData = res.docs
        .map((x, i) => ({
          ...x.data(),
          key: i,
          list: x.data().planCode === 'ims' ? 'Lead' : 'Customer', 
          source_name: sourceNameLookup(x.data().affiliate),
          offer: x.data().planCode || 'n/a',
          litigator: x.data().isLitigator === true ? "Yes" : "No",
        }))

      applyFilters(tableData, filters)
    } catch (error) {

      console.error({ error })

    } finally {

      // remove loading
      setIsFetching(false)
    }
  }

  const getColumnSearchProps = dataIndex => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
      <div style={{ padding: 8 }}>
        <Input
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{ marginBottom: 8, display: 'block' }}
        />
        <Space>
          <Button
            type='primary'
            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
            icon={<SearchOutlined />}
            size='small'
            style={{ width: 90 }}
          >
            Search
          </Button>
          <Button onClick={() => handleReset(clearFilters)} size='small' style={{ width: 90 }}>
            Reset
          </Button>
          <Button
            type='link'
            size='small'
            onClick={() => {
              confirm({ closeDropdown: false })
              setSearchText(selectedKeys[0])
              setSearchedColumn(dataIndex)
            }}
          >
            Filter
          </Button>
        </Space>
      </div>
    ),
    filterIcon: filtered => <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />,
    filteredValue: filters[dataIndex] || null,
    render: text =>
      searchedColumn === dataIndex ? (
        <Highlighter
          highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
          searchWords={[searchText]}
          autoEscape
          textToHighlight={text ? text.toString() : ''}
        />
      ) : (
        text
      ),
  })

  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm()
    setSearchText(selectedKeys[0])
    setSearchedColumn(dataIndex)
  }

  const handleReset = clearFilters => {
    clearFilters()
    setSearchText('')
  }

  const resetFilters = () => {
    setFilters({ shared_to: undefined, received_from: undefined })
  }

  const toggleFilter = (name, value) => {
    const f = { ...filters }
    let updatedFilters

    if (value === undefined || Array.isArray(value)) {
      // select all or clear
      updatedFilters = { ...f, [name]: value }
    } else if (!filters[name]) {
      // no filter yet: add all but this one
      const values = groupedTotals[name]?.map(x => x.name)?.filter(x => x != value)
      updatedFilters = { ...f, [name]: values }
    } else if (filters[name]?.includes(value)) {
      // remove
      const values = filters[name]?.filter(x => x !== value)
      updatedFilters = { ...f, [name]: values }
    } else {
      // add
      const values = [...f[name], value]

      updatedFilters = {
        ...f,
        [name]: values.length === groupedTotals[name]?.length ? null : values
      }
    }

    setFilters(updatedFilters)
    applyFilters(historyTable, updatedFilters)
  }

  const columns = [
    {
      width: 150,
      key: 'created_at',
      title: 'Subscribed on',
      dataIndex: 'created_at',
      defaultSortOrder: 'descend',
      sorter: (a, b) => a.created_at - b.created_at,
      render: text => `${moment(text.toDate()).format('MM/DD/YY hh:mm A')}`
    }, {
      width: 150,
      key: 'cleanedPhoneNumber',
      title: 'Phone Number',
      dataIndex: 'cleanedPhoneNumber',
      sorter: (a, b) => a.email.localeCompare(b.cleanedPhoneNumber),
      ...getColumnSearchProps('cleanedPhoneNumber')    
    }, {
      width: 150,
      key: 'source_name',
      title: 'Source',
      dataIndex: 'source_name',
      sorter: (a, b) => a.source_name.localeCompare(b.source_name),
      ...getColumnSearchProps('source_name')
    }, {
      width: 100,
      key: 'litigator',
      title: 'Litigator',
      dataIndex: 'litigator',
      sorter: (a, b) => a.litigator.localeCompare(b.litigator),
      ...getColumnSearchProps('litigator')
    }, {
      width: 150,
      key: 'planCode',
      title: 'Plan',
      dataIndex: 'planCode',
      sorter: (a, b) => a.planCode.localeCompare(b.planCode),
      ...getColumnSearchProps('planCode')
    }, {
      width: 100,
      key: 'list',
      title: 'Type',
      dataIndex: 'list',
      sorter: (a, b) => a.list.localeCompare(b.list),
      ...getColumnSearchProps('list')
    }
  ]

  return (
    <>
      <Title level={2}>SMS</Title>
      
      <Row gutter={[16, 16]} justify='end' align='center'>
        <Col sm={8} md={4} >
          <Button
            type='primary'
            loading={isFetching}
            onClick={fetchHistory}
          >
            Refresh Data
          </Button>
        </Col>
      </Row>

      {(isFetching) &&
        <Skeleton active={true} paragraph={{ rows: 1 }} style={{ marginTop: '-1.25rem' }} />
      }

      {!(isFetching) &&
        <Row gutter={[16, 16]} justify='space-between' align='middle'>
          <Col xm={24} sm={16} style={{ display: 'flex', flexDirection: 'column' }}>
            <DateRangeSelector fetchStartDate={fetchStartDate} fetchEndDate={fetchEndDate} onChange={({ startDate, endDate }) => { 
              resetFilters(); 
              setFetchStartDate(startDate)
              setFetchEndDate(endDate)
            }}/>
          </Col>
        </Row>
      }

      {groupedTotals.list?.length > 0 &&
        <>
          <Divider orientation='left'>Types<Text type='secondary' style={{ marginBottom: '1em', fontWeight: 'normal' }}> - <a onClick={() => toggleFilter('list', undefined)}>Select all</a> | <a onClick={() => toggleFilter('list', [])} >Clear</a></Text></Divider>
          <Row gutter={[16, 16]}>
            <Col xs={12} sm={8} lg={6} xl={4} xxl={3}>
              <Card
                size='small'
                hoverable={true}
                title='All'
                onClick={() => !filters?.list ? toggleFilter('list', []) : toggleFilter('list', undefined)}
              >
                <Statistic title='Total' value={filteredTable.length} />
                <Statistic title='Unique' value={determineAllUnqiue(filteredTable)} />
              </Card>
            </Col>
            {groupedTotals.list?.map((x, i) =>
              <Col key={i} xs={12} sm={8} lg={6} xl={4} xxl={3}>
                <Card
                  size='small'
                  hoverable={true}
                  title={x.title ? <Tooltip title={x.title}>{x.name}</Tooltip> : x.name}
                  style={{ cursor: 'pointer', opacity: !filters.list || filters.list?.includes(x.name) ? "1" : "0.3" }}
                  onClick={() => toggleFilter('list', x.name)}
                >
                  <Statistic title='Total' value={x.total} />
                  <Statistic title='Unique' value={x.unique} />
                </Card>
              </Col>
            )}
          </Row>
        </>
      }

      {groupedTotals.source_name?.length > 0 &&
        <>
          <Divider orientation='left'>Sources<Text type='secondary' style={{ marginBottom: '1em', fontWeight: 'normal' }}> - <a onClick={() => toggleFilter('source_name', undefined)}>Select all</a> | <a onClick={() => toggleFilter('source_name', [])} >Clear</a></Text></Divider>
          <Row gutter={[16, 16]}>
            {groupedTotals.source_name?.map((x, i) =>
              <Col key={i} xs={12} sm={8} lg={6} xl={4} xxl={3}>
                <Card
                  size='small'
                  hoverable={true}
                  title={x.title ? <Tooltip title={x.title}>{x.name}</Tooltip> : x.name}
                  style={{ cursor: 'pointer', opacity: !filters.source_name || filters.source_name?.includes(x.name) ? "1" : "0.3" }}
                  onClick={() => toggleFilter('source_name', x.name)}
                >
                  <Statistic title='Total' value={x.total} />
                  <Statistic title='Unique' value={x.unique} />
                </Card>
              </Col>
            )}
          </Row>
        </>
      }

      {groupedTotals.offer?.length > 0 &&
        <>
          <Divider orientation='left'>Plans<Text type='secondary' style={{ marginBottom: '1em', fontWeight: 'normal' }}> - <a onClick={() => toggleFilter('offer', undefined)}>Select all</a> | <a onClick={() => toggleFilter('offer', [])} >Clear</a></Text></Divider>
          <Row gutter={[16, 16]}>
            {groupedTotals.offer?.map((x, i) =>
              <Col key={i} xs={12} sm={8} lg={6} xl={4} xxl={3}>
                <Card
                  size='small'
                  hoverable={true}
                  title={x.title ? <Tooltip title={x.title}>{x.name}</Tooltip> : x.name}
                  style={{ cursor: 'pointer', opacity: !filters.offer || filters.offer?.includes(x.name) ? "1" : "0.3" }}
                  onClick={() => toggleFilter('offer', x.name)}
                >
                  <Statistic title='Total' value={x.total} />
                  <Statistic title='Unique' value={x.unique} />
                </Card>
              </Col>
            )}
          </Row>
        </>
      }

      <Row justify='space-between' align='middle'>
        <Table
          style={{ paddingTop: 20 }}
          onChange={(pagination, filters, sorter, extra) => {
            setFilters(filters)
            applyFilters(historyTable, filters)
          }}
          loading={isFetching}
          columns={columns}
          dataSource={filteredTable}
          scroll={{ x: 1000, y: 500 }}
          size='small'
          title={() => (
            <CSVLink
              filename={'OptiPub_SMS.csv'}
              data={filteredTable}
              className='btn btn-primary'
            >
              Export to CSV
            </CSVLink>
          )}
        />
      </Row>
    </>
  )
}

export default LeadsIn